/**
 * Internal dependencies
 */
import getComputedStyle from './get-computed-style';
import getRangeHeight from './get-range-height';
import getRectangleFromRange from './get-rectangle-from-range';
import isSelectionForward from './is-selection-forward';
import hiddenCaretRangeFromPoint from './hidden-caret-range-from-point';
import { assertIsDefined } from '../utils/assert-is-defined';
import isInputOrTextArea from './is-input-or-text-area';
/**
 * Check whether the selection is at the edge of the container. Checks for
 * horizontal position by default. Set `onlyVertical` to true to check only
 * vertically.
 *
 * @param {Element} container Focusable element.
 * @param {boolean} isReverse Set to true to check left, false to check right.
 * @param {boolean} [onlyVertical=false] Set to true to check only vertical position.
 *
 * @return {boolean} True if at the edge, false if not.
 */

export default function isEdge(container, isReverse) {
  var onlyVertical = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  if (isInputOrTextArea(container)) {
    if (container.selectionStart !== container.selectionEnd) {
      return false;
    }

    if (isReverse) {
      return container.selectionStart === 0;
    }

    return container.value.length === container.selectionStart;
  }

  if (!
  /** @type {HTMLElement} */
  container.isContentEditable) {
    return true;
  }

  var ownerDocument = container.ownerDocument;
  var defaultView = ownerDocument.defaultView;
  assertIsDefined(defaultView, 'defaultView');
  var selection = defaultView.getSelection();

  if (!selection || !selection.rangeCount) {
    return false;
  }

  var range = selection.getRangeAt(0);
  var collapsedRange = range.cloneRange();
  var isForward = isSelectionForward(selection);
  var isCollapsed = selection.isCollapsed; // Collapse in direction of selection.

  if (!isCollapsed) {
    collapsedRange.collapse(!isForward);
  }

  var collapsedRangeRect = getRectangleFromRange(collapsedRange);
  var rangeRect = getRectangleFromRange(range);

  if (!collapsedRangeRect || !rangeRect) {
    return false;
  } // Only consider the multiline selection at the edge if the direction is
  // towards the edge. The selection is multiline if it is taller than the
  // collapsed  selection.


  var rangeHeight = getRangeHeight(range);

  if (!isCollapsed && rangeHeight && rangeHeight > collapsedRangeRect.height && isForward === isReverse) {
    return false;
  } // In the case of RTL scripts, the horizontal edge is at the opposite side.


  var _getComputedStyle = getComputedStyle(container),
      direction = _getComputedStyle.direction;

  var isReverseDir = direction === 'rtl' ? !isReverse : isReverse;
  var containerRect = container.getBoundingClientRect(); // To check if a selection is at the edge, we insert a test selection at the
  // edge of the container and check if the selections have the same vertical
  // or horizontal position. If they do, the selection is at the edge.
  // This method proves to be better than a DOM-based calculation for the
  // horizontal edge, since it ignores empty textnodes and a trailing line
  // break element. In other words, we need to check visual positioning, not
  // DOM positioning.
  // It also proves better than using the computed style for the vertical
  // edge, because we cannot know the padding and line height reliably in
  // pixels. `getComputedStyle` may return a value with different units.

  var x = isReverseDir ? containerRect.left + 1 : containerRect.right - 1;
  var y = isReverse ? containerRect.top + 1 : containerRect.bottom - 1;
  var testRange = hiddenCaretRangeFromPoint(ownerDocument, x, y,
  /** @type {HTMLElement} */
  container);

  if (!testRange) {
    return false;
  }

  var testRect = getRectangleFromRange(testRange);

  if (!testRect) {
    return false;
  }

  var verticalSide = isReverse ? 'top' : 'bottom';
  var horizontalSide = isReverseDir ? 'left' : 'right';
  var verticalDiff = testRect[verticalSide] - rangeRect[verticalSide];
  var horizontalDiff = testRect[horizontalSide] - collapsedRangeRect[horizontalSide]; // Allow the position to be 1px off.

  var hasVerticalDiff = Math.abs(verticalDiff) <= 1;
  var hasHorizontalDiff = Math.abs(horizontalDiff) <= 1;
  return onlyVertical ? hasVerticalDiff : hasVerticalDiff && hasHorizontalDiff;
}
//# sourceMappingURL=is-edge.js.map